"use client";

import { useId, useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

interface NavSvgIconProps {
  size?: number;
  className?: string;
  play?: boolean;
}

const VERTEX_A = { cx: 19.36, cy: 3.83 }; // top
const VERTEX_B = { cx: 21.13, cy: 21.33 }; // bottom-right
const VERTEX_C = { cx: 3.83, cy: 13.14 }; // bottom-left

const CIRCLE_RADIUS = 3.68;
const MASK_RADIUS = 3.83;
const SEGMENT_DURATION = 3.125;

export function CapabilitiesIcon({ size = 66, className, play = true }: NavSvgIconProps) {
  const reactId = useId();
  const maskId = `capabilities-line-mask-${reactId.replace(/:/g, "")}`;

  const containerRef = useRef<SVGSVGElement | null>(null);
  const cARef = useRef<SVGCircleElement | null>(null);
  const cBRef = useRef<SVGCircleElement | null>(null);
  const cCRef = useRef<SVGCircleElement | null>(null);
  const maskARef = useRef<SVGCircleElement | null>(null);
  const maskBRef = useRef<SVGCircleElement | null>(null);
  const maskCRef = useRef<SVGCircleElement | null>(null);
  const lineABRef = useRef<SVGLineElement | null>(null);
  const lineBCRef = useRef<SVGLineElement | null>(null);
  const lineCARef = useRef<SVGLineElement | null>(null);

  useGSAP(
    () => {
      if (!play) return;
      const cA = cARef.current;
      const cB = cBRef.current;
      const cC = cCRef.current;
      const maskA = maskARef.current;
      const maskB = maskBRef.current;
      const maskC = maskCRef.current;
      const lineAB = lineABRef.current;
      const lineBC = lineBCRef.current;
      const lineCA = lineCARef.current;
      if (!cA || !cB || !cC || !maskA || !maskB || !maskC || !lineAB || !lineBC || !lineCA) return;

      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const ease = "sine.inOut";
        const state = {
          pA: { ...VERTEX_A },
          pB: { ...VERTEX_B },
          pC: { ...VERTEX_C },
        };

        const apply = () => {
          const ax = state.pA.cx.toFixed(3);
          const ay = state.pA.cy.toFixed(3);
          const bx = state.pB.cx.toFixed(3);
          const by = state.pB.cy.toFixed(3);
          const cx = state.pC.cx.toFixed(3);
          const cy = state.pC.cy.toFixed(3);

          cA.setAttribute("cx", ax);
          cA.setAttribute("cy", ay);
          maskA.setAttribute("cx", ax);
          maskA.setAttribute("cy", ay);

          cB.setAttribute("cx", bx);
          cB.setAttribute("cy", by);
          maskB.setAttribute("cx", bx);
          maskB.setAttribute("cy", by);

          cC.setAttribute("cx", cx);
          cC.setAttribute("cy", cy);
          maskC.setAttribute("cx", cx);
          maskC.setAttribute("cy", cy);

          lineAB.setAttribute("x1", ax);
          lineAB.setAttribute("y1", ay);
          lineAB.setAttribute("x2", bx);
          lineAB.setAttribute("y2", by);

          lineBC.setAttribute("x1", bx);
          lineBC.setAttribute("y1", by);
          lineBC.setAttribute("x2", cx);
          lineBC.setAttribute("y2", cy);

          lineCA.setAttribute("x1", cx);
          lineCA.setAttribute("y1", cy);
          lineCA.setAttribute("x2", ax);
          lineCA.setAttribute("y2", ay);
        };

        const tl = gsap.timeline({ repeat: -1, onUpdate: apply });

        // Segment 1: A→B, B→C, C→A
        tl.to(state.pA, { cx: VERTEX_B.cx, cy: VERTEX_B.cy, duration: SEGMENT_DURATION, ease }, 0);
        tl.to(state.pB, { cx: VERTEX_C.cx, cy: VERTEX_C.cy, duration: SEGMENT_DURATION, ease }, 0);
        tl.to(state.pC, { cx: VERTEX_A.cx, cy: VERTEX_A.cy, duration: SEGMENT_DURATION, ease }, 0);

        // Segment 2: A→C, B→A, C→B (continue cycling)
        tl.to(state.pA, { cx: VERTEX_C.cx, cy: VERTEX_C.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION);
        tl.to(state.pB, { cx: VERTEX_A.cx, cy: VERTEX_A.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION);
        tl.to(state.pC, { cx: VERTEX_B.cx, cy: VERTEX_B.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION);

        // Segment 3: back to home
        tl.to(state.pA, { cx: VERTEX_A.cx, cy: VERTEX_A.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION * 2);
        tl.to(state.pB, { cx: VERTEX_B.cx, cy: VERTEX_B.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION * 2);
        tl.to(state.pC, { cx: VERTEX_C.cx, cy: VERTEX_C.cy, duration: SEGMENT_DURATION, ease }, SEGMENT_DURATION * 2);
      });

      return () => mm.revert();
    },
    { scope: containerRef, dependencies: [play] }
  );

  return (
    <svg
      ref={containerRef}
      viewBox="0 0 24.96 25.16"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.3"
      aria-hidden="true"
    >
      <defs>
        <mask id={maskId}>
          <rect x="0" y="0" width="24.96" height="25.16" fill="white" />
          <circle ref={maskARef} cx={VERTEX_A.cx} cy={VERTEX_A.cy} r={MASK_RADIUS} fill="black" />
          <circle ref={maskBRef} cx={VERTEX_B.cx} cy={VERTEX_B.cy} r={MASK_RADIUS} fill="black" />
          <circle ref={maskCRef} cx={VERTEX_C.cx} cy={VERTEX_C.cy} r={MASK_RADIUS} fill="black" />
        </mask>
      </defs>

      <g mask={`url(#${maskId})`}>
        <line
          ref={lineABRef}
          x1={VERTEX_A.cx}
          y1={VERTEX_A.cy}
          x2={VERTEX_B.cx}
          y2={VERTEX_B.cy}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          ref={lineBCRef}
          x1={VERTEX_B.cx}
          y1={VERTEX_B.cy}
          x2={VERTEX_C.cx}
          y2={VERTEX_C.cy}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          ref={lineCARef}
          x1={VERTEX_C.cx}
          y1={VERTEX_C.cy}
          x2={VERTEX_A.cx}
          y2={VERTEX_A.cy}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      <circle ref={cARef} cx={VERTEX_A.cx} cy={VERTEX_A.cy} r={CIRCLE_RADIUS} strokeMiterlimit="10" />
      <circle ref={cBRef} cx={VERTEX_B.cx} cy={VERTEX_B.cy} r={CIRCLE_RADIUS} strokeMiterlimit="10" />
      <circle ref={cCRef} cx={VERTEX_C.cx} cy={VERTEX_C.cy} r={CIRCLE_RADIUS} strokeMiterlimit="10" />
    </svg>
  );
}
